import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { createOrganization, clearError } from '../redux/slices/organizationSlice';

const CreateOrganization: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.organization);
  const currentUser = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    country: ''
  });

  // Check if user has Loop domain
  const isLoopUser = currentUser?.email?.toLowerCase().endsWith('@loop.com.na');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await dispatch(createOrganization(formData)).unwrap();
      navigate('/organization/settings');
    } catch (err) {
      // Error is handled by Redux
    }
  };

  if (!isLoopUser) {
    return (
      <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem' }}>
        <Container>
          <Card style={{
            border: 'none',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <Card.Body style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{
                fontSize: '48px',
                color: '#ff4d4f',
                marginBottom: '20px'
              }}>
                🚫
              </div>
              <h3 style={{ color: '#00579a', marginBottom: '16px' }}>
                Access Denied
              </h3>
              <p style={{ color: '#595959', fontSize: '16px', marginBottom: '24px' }}>
                Only Loop domain users (@loop.com.na) can create new organizations.
              </p>
              <Button
                onClick={() => navigate('/')}
                style={{
                  backgroundColor: '#00579a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 24px'
                }}
              >
                Go to Dashboard
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="mb-4">
          <h1 style={{
            color: '#00579a',
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            Create New Organization
          </h1>
          <p style={{ color: '#595959', marginTop: '8px' }}>
            Set up a new organization to manage users, content, and settings.
          </p>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => dispatch(clearError())}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '1.5rem'
            }}
          >
            {error}
          </Alert>
        )}

        <Alert variant="info" style={{ marginBottom: '1.5rem' }}>
          <strong>Note:</strong> You are creating an organization as a Loop administrator. 
          The organization domain will be used to validate user email addresses.
        </Alert>

        <Card style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)'
        }}>
          <Card.Body style={{ padding: '24px' }}>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <h6 style={{ color: '#00579a', fontWeight: 600, marginBottom: '16px' }}>
                    Basic Information
                  </h6>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Organization Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      style={inputStyle}
                      placeholder="e.g., Acme Corporation"
                    />
                    <Form.Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      The official name of the organization
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Domain *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value.toLowerCase() })}
                      required
                      style={inputStyle}
                      placeholder="example.com"
                    />
                    <Form.Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      Email domain for users (e.g., example.com). Users must have emails like user@example.com
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Contact Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      style={inputStyle}
                      placeholder="contact@example.com"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Contact Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      style={inputStyle}
                      placeholder="+264 61 123 4567"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <h6 style={{ color: '#00579a', fontWeight: 600, marginBottom: '16px' }}>
                    Location Details
                  </h6>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      style={inputStyle}
                      placeholder="Street address"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      style={inputStyle}
                      placeholder="Windhoek"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Country</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      style={inputStyle}
                      placeholder="Namibia"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  style={{
                    backgroundColor: 'white',
                    color: '#595959',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  style={{
                    backgroundColor: '#00579a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(0,87,154,0.2)'
                  }}
                >
                  {loading ? 'Creating...' : 'Create Organization'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontWeight: 500,
  color: '#262626',
  fontSize: '14px',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px'
};

export default CreateOrganization;